import { formatNumber, formatPercent, useI18n, type Lang } from '../i18n';
import { Icon, Spinner } from '../ui/Icon';
import type { CompileUi, EngineUi } from './engineUi';

/** Engine state at a glance: "Le moteur prépare ses aiguilles… 42 %" → "Prêt à vérifier" → "Vérifié en 12 ms". */
export function EnginePill({ engine, compile, isMock }: { engine: EngineUi; compile: CompileUi; isMock: boolean }) {
  const { t, lang } = useI18n();
  let tone = 'text-muted';
  let icon = <Spinner />;
  let label: string;
  let percent: number | undefined;

  if (engine.status === 'loading') {
    percent = engine.progress?.percent;
    label = `${t('engine.loading')}${percent !== undefined ? ` ${formatPercent(lang, percent)}` : ''}`;
  } else if (engine.status === 'unavailable') {
    tone = 'text-error';
    icon = <Icon name="x" size={12} />;
    label = t('engine.unavailable');
  } else if (compile.status === 'compiling') {
    label = t('engine.compiling');
  } else if (compile.status === 'error') {
    tone = 'text-error';
    icon = <Icon name="x" size={12} />;
    label = t('engine.error');
  } else if (compile.status === 'done') {
    tone = 'text-accent-strong';
    icon = <Icon name="check" size={12} />;
    label = t('engine.compiled', { ms: formatMs(compile.ms, lang) });
  } else {
    tone = 'text-accent-strong';
    icon = <Icon name="check" size={12} />;
    label = t('engine.ready');
  }

  const title =
    engine.status === 'ready'
      ? `${t('engine.readyVersion', { version: engine.info.engineVersion })}${isMock ? ` · ${t('engine.mock')}` : ''}`
      : label;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('engine.statusLabel', { label })}
      title={title}
      data-testid="engine-pill"
      className={`relative inline-flex h-8 max-w-full items-center gap-2 overflow-hidden rounded-pill px-2 text-xs font-semibold tabular ${tone}`}
    >
      {icon}
      <span className="truncate">{label}</span>
      {isMock && engine.status === 'ready' && (
        <span className="rounded-pill bg-warning/15 px-2 text-2xs font-bold uppercase tracking-wide text-warning">{t('engine.mockShort')}</span>
      )}
      {percent !== undefined && engine.status === 'loading' && (
        <span className="absolute inset-x-2 bottom-[3px] h-[2px] overflow-hidden rounded-pill bg-line" aria-hidden="true">
          <span className="block h-full rounded-pill bg-accent transition-[width] duration-300" style={{ width: `${percent}%` }} />
        </span>
      )}
    </div>
  );
}

export function formatMs(ms: number, lang: Lang = 'fr'): string {
  return ms < 10 ? formatNumber(lang, Math.round(ms * 10) / 10, 1) : formatNumber(lang, Math.round(ms), 0);
}
